import React, { useContext, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useForm, FormProvider } from 'react-hook-form';
import PurchaseFormContext from './PurchaseFormContext';
import CheckoutPageContext from '../../../context/CheckoutPageContext';
// Import the centralized validation helper
import { getValidationFunctions } from '../../../tools/validations/formValidation';

export default function PurchaseFormProvider({ children }) {
  const { checkoutPageState } = useContext(CheckoutPageContext) || {};
  const { orderContact, checkoutFormConfig, userData } = checkoutPageState || {};

  const methods = useForm({
    mode: 'onBlur',
  });

  const { reset } = methods;

  useEffect(() => {
    if (orderContact) {
      const guestCustomerMailId = sessionStorage?.getItem('guestCustomerMailId');
      const email = (guestCustomerMailId && !userData?.isLoggedIn) ? guestCustomerMailId : orderContact.email || '';

      reset({
        addresses: {
          [orderContact.addressType]: {
            email,
            phone: orderContact.phoneNumber || '',
          },
        },
        smsOptIn: false,
        ...(checkoutFormConfig?.defaultValues || {}),
      });
    }
  }, [orderContact, checkoutFormConfig, reset, userData]);

  // Prepare validation functions using the helper and context data
  const validationFunctions = useMemo(() => {
    // Pass error messages from orderContact or fallback
    const validationConfig = {
      invalidEmailMessage: orderContact?.invalidEmailLabel?.value,
      invalidPhoneMessage: orderContact?.invalidPhoneLabel?.value,
      // Add other error messages or config needed by validators here
    };
    return getValidationFunctions(validationConfig);
  }, [orderContact]); // Re-create if orderContact (containing labels) changes

  // Bundle methods and validation functions together in your context
  const contextValue = useMemo(() => ({
    methods,
    validationFunctions, // Add the prepared validation functions
  }), [methods, validationFunctions]);

  // Always render the provider, even if orderContact is initially null/undefined
  // Components consuming the context should handle the loading state if necessary
  return (
    <PurchaseFormContext.Provider value={contextValue}>
      <FormProvider {...methods}>
        {children}
      </FormProvider>
    </PurchaseFormContext.Provider>
  );
}

PurchaseFormProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
